import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  Autocomplete,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Badge,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Label as LabelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import axios from 'axios';
import UseToast from '../UseToast';
import { getLanguageText, setLanguageText } from '../../utils/multilanguageUtils';

const TagManager = ({ userId, buttonColor, currentLanguage = 'en' }) => {
  const [tags, setTags] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tagLocations, setTagLocations] = useState({}); // Map of tagId -> locations array
  const [expandedTags, setExpandedTags] = useState({}); // Track which tags are expanded
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkAssignDialog, setOpenBulkAssignDialog] = useState(false);
  const [openLocationsDialog, setOpenLocationsDialog] = useState(false);
  const [openAssignLocationsDialog, setOpenAssignLocationsDialog] = useState(false);
  const [selectedTagForLocations, setSelectedTagForLocations] = useState(null);
  const [selectedTagForAssignment, setSelectedTagForAssignment] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  
  // Form state
  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');
  const [tagColor, setTagColor] = useState('#3498db');
  
  // Bulk assign state
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  
  // Assign locations to specific tag state
  const [locationsToAssign, setLocationsToAssign] = useState([]);
  
  const { showMsgSuccessToast, showErrorToast } = UseToast();

  // Fetch tags
  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/tags/${userId}`);
      if (response.data.success) {
        setTags(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      showErrorToast('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  // Fetch locations
  const fetchLocations = async () => {
    try {
      const response = await axios.get(`/api/wayfinder/get-locations/${userId}`);
      if (response.data.success) {
        const allLocations = response.data.data || [];
        console.log('Fetched locations:', allLocations.length);
        setLocations(allLocations);
        
        // Build a map of tag -> locations
        const tagLocationMap = {};
        
        // Initialize empty arrays for all existing tags
        tags.forEach(tag => {
          tagLocationMap[tag._id] = [];
        });
        
        // Populate the map with locations
        allLocations.forEach(location => {
          if (location.tags && Array.isArray(location.tags)) {
            location.tags.forEach(tagId => {
              const tagIdStr = typeof tagId === 'object' ? tagId._id : tagId;
              if (!tagLocationMap[tagIdStr]) {
                tagLocationMap[tagIdStr] = [];
              }
              tagLocationMap[tagIdStr].push(location);
            });
          }
        });
        
        console.log('Updated tagLocationMap:', tagLocationMap);
        setTagLocations(tagLocationMap);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchTags();
      fetchLocations();
    }
  }, [userId]);

  // Handle create/update tag
  const handleSaveTag = async () => {
    if (!tagName.trim()) {
      showErrorToast('Tag name is required');
      return;
    }

    try {
      setLoading(true);
      
      if (editingTag) {
        // Update existing tag
        const response = await axios.put(`/api/tags/${userId}/${editingTag._id}`, {
          name: tagName,
          description: tagDescription,
          color: tagColor,
          currentLanguage,
        });
        
        if (response.data.success) {
          showMsgSuccessToast('Tag updated successfully');
          fetchTags();
        }
      } else {
        // Create new tag
        const response = await axios.post(`/api/tags/${userId}`, {
          name: tagName,
          description: tagDescription,
          color: tagColor,
          currentLanguage,
        });
        
        if (response.data.success) {
          showMsgSuccessToast('Tag created successfully');
          fetchTags();
        }
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving tag:', error);
      showErrorToast('Failed to save tag');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete tag
  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Are you sure you want to delete this tag? It will be removed from all locations.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(`/api/tags/${userId}/${tagId}`);
      
      if (response.data.success) {
        showMsgSuccessToast('Tag deleted successfully');
        fetchTags();
      }
    } catch (error) {
      console.error('Error deleting tag:', error);
      showErrorToast('Failed to delete tag');
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk assign tags to locations
  const handleBulkAssign = async () => {
    if (selectedTags.length === 0 || selectedLocations.length === 0) {
      showErrorToast('Please select at least one tag and one location');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`/api/tags/${userId}/bulk-assign`, {
        tagIds: selectedTags.map(tag => tag._id),
        locationIds: selectedLocations.map(loc => loc._id),
      });
      
      if (response.data.success) {
        showMsgSuccessToast(`Tags assigned to ${response.data.data.modifiedCount} locations`);
        handleCloseBulkAssignDialog();
        // Refresh locations to update the tag-location mapping
        fetchLocations();
      }
    } catch (error) {
      console.error('Error bulk assigning tags:', error);
      showErrorToast('Failed to assign tags');
    } finally {
      setLoading(false);
    }
  };

  // Toggle tag expansion
  const handleToggleExpand = (tagId) => {
    setExpandedTags(prev => ({
      ...prev,
      [tagId]: !prev[tagId]
    }));
  };

  // Open locations dialog for a specific tag
  const handleViewLocations = (tag) => {
    setSelectedTagForLocations(tag);
    setOpenLocationsDialog(true);
  };

  // Close locations dialog
  const handleCloseLocationsDialog = () => {
    setOpenLocationsDialog(false);
    setSelectedTagForLocations(null);
  };

  // Remove tag from a specific location
  const handleRemoveTagFromLocation = async (tagId, locationId) => {
    try {
      setLoading(true);
      
      // Get the location's current tags
      const location = locations.find(loc => loc._id === locationId);
      if (!location) {
        showErrorToast('Location not found');
        setLoading(false);
        return;
      }
      
      // Filter out the tag to be removed
      const updatedTags = (location.tags || [])
        .map(tag => typeof tag === 'object' ? tag._id : tag)
        .filter(id => id !== tagId);
      
      console.log('Removing tag:', tagId, 'from location:', locationId);
      console.log('Current tags:', location.tags);
      console.log('Updated tags:', updatedTags);
      
      // Update the location with new tags (can be empty array)
      const response = await axios.post(`/api/tags/${userId}/location/${locationId}/assign`, {
        tagIds: updatedTags,
      });
      
      if (response.data.success) {
        showMsgSuccessToast('Tag removed from location');
        
        // Immediately update the local state to reflect the change
        setLocations(prevLocations => 
          prevLocations.map(loc => 
            loc._id === locationId 
              ? { ...loc, tags: updatedTags }
              : loc
          )
        );
        
        // Update tagLocations map
        setTagLocations(prevTagLocations => {
          const newTagLocations = { ...prevTagLocations };
          
          // Remove this location from the tag's location list
          if (newTagLocations[tagId]) {
            newTagLocations[tagId] = newTagLocations[tagId].filter(
              loc => loc._id !== locationId
            );
          }
          
          return newTagLocations;
        });
        
        // Refresh locations from server to ensure consistency
        await fetchLocations();
      }
    } catch (error) {
      console.error('Error removing tag from location:', error);
      showErrorToast(error.response?.data?.message || 'Failed to remove tag');
    } finally {
      setLoading(false);
    }
  };

  // Dialog handlers
  const handleOpenDialog = (tag = null) => {
    if (tag) {
      setEditingTag(tag);
      setTagName(getLanguageText(tag.name, currentLanguage) || '');
      setTagDescription(getLanguageText(tag.description, currentLanguage) || '');
      setTagColor(tag.color || '#3498db');
    } else {
      setEditingTag(null);
      setTagName('');
      setTagDescription('');
      setTagColor('#3498db');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTag(null);
    setTagName('');
    setTagDescription('');
    setTagColor('#3498db');
  };

  const handleOpenBulkAssignDialog = () => {
    setSelectedTags([]);
    setSelectedLocations([]);
    setOpenBulkAssignDialog(true);
  };

  const handleCloseBulkAssignDialog = () => {
    setOpenBulkAssignDialog(false);
    setSelectedTags([]);
    setSelectedLocations([]);
  };

  // Open assign locations dialog for a specific tag
  const handleOpenAssignLocationsDialog = (tag) => {
    setSelectedTagForAssignment(tag);
    
    // Pre-select locations that already have this tag
    const assignedLocations = tagLocations[tag._id] || [];
    setLocationsToAssign(assignedLocations);
    
    setOpenAssignLocationsDialog(true);
  };

  // Close assign locations dialog
  const handleCloseAssignLocationsDialog = () => {
    setOpenAssignLocationsDialog(false);
    setSelectedTagForAssignment(null);
    setLocationsToAssign([]);
  };

  // Assign locations to a specific tag
  const handleAssignLocationsToTag = async () => {
    if (!selectedTagForAssignment) {
      showErrorToast('No tag selected');
      return;
    }

    try {
      setLoading(true);
      
      // Get all locations that currently have this tag
      const currentLocationsWithTag = tagLocations[selectedTagForAssignment._id] || [];
      const currentLocationIds = currentLocationsWithTag.map(loc => loc._id);
      
      // Get the new selection of location IDs
      const newLocationIds = locationsToAssign.map(loc => loc._id);
      
      console.log('Current locations with tag:', currentLocationIds);
      console.log('New locations to assign:', newLocationIds);
      
      // Find locations to add the tag to (in new selection but not in current)
      const locationsToAdd = newLocationIds.filter(id => !currentLocationIds.includes(id));
      
      // Find locations to remove the tag from (in current but not in new selection)
      const locationsToRemove = currentLocationIds.filter(id => !newLocationIds.includes(id));
      
      console.log('Locations to add tag:', locationsToAdd);
      console.log('Locations to remove tag:', locationsToRemove);
      
      // Add tag to new locations
      if (locationsToAdd.length > 0) {
        await axios.post(`/api/tags/${userId}/bulk-assign`, {
          tagIds: [selectedTagForAssignment._id],
          locationIds: locationsToAdd,
        });
      }
      
      // Remove tag from deselected locations
      for (const locationId of locationsToRemove) {
        const location = locations.find(loc => loc._id === locationId);
        if (location) {
          const updatedTags = (location.tags || [])
            .map(tag => typeof tag === 'object' ? tag._id : tag)
            .filter(id => id !== selectedTagForAssignment._id);
          
          await axios.post(`/api/tags/${userId}/location/${locationId}/assign`, {
            tagIds: updatedTags,
          });
        }
      }
      
      const totalChanges = locationsToAdd.length + locationsToRemove.length;
      showMsgSuccessToast(
        totalChanges > 0 
          ? `Updated ${totalChanges} location(s)` 
          : 'No changes made'
      );
      
      handleCloseAssignLocationsDialog();
      await fetchLocations();
    } catch (error) {
      console.error('Error assigning locations to tag:', error);
      showErrorToast('Failed to assign locations');
    } finally {
      setLoading(false);
    }
  };

  // Predefined colors
  const colorOptions = [
    '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6',
    '#1abc9c', '#34495e', '#e67e22', '#95a5a6', '#d35400'
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Tag Manager
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<LabelIcon />}
            onClick={handleOpenBulkAssignDialog}
            sx={{
              borderColor: buttonColor,
              color: buttonColor,
              '&:hover': {
                borderColor: buttonColor,
                backgroundColor: `${buttonColor}10`,
              },
            }}
          >
            Bulk Assign Tags
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: buttonColor,
              '&:hover': {
                backgroundColor: buttonColor,
                opacity: 0.9,
              },
            }}
          >
            Create Tag
          </Button>
        </Box>
      </Box>

      {loading && tags.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : tags.length === 0 ? (
        <Alert severity="info">
          No tags created yet. Click "Create Tag" to get started.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {tags.map((tag) => {
            const assignedLocations = tagLocations[tag._id] || [];
            const isExpanded = expandedTags[tag._id] || false;
            
            return (
              <Grid item xs={12} sm={6} md={4} key={tag._id}>
                <Card
                  sx={{
                    position: 'relative',
                    overflow: 'visible',
                    borderRadius: 3,
                    border: 'none',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 40px ${tag.color}40`,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '6px',
                      background: `linear-gradient(135deg, ${tag.color} 0%, ${tag.color}dd 100%)`,
                      borderRadius: '12px 12px 0 0',
                    },
                  }}
                >
                  <CardContent sx={{ pt: 3, pb: 2 }}>
                    {/* Header Section */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2.5,
                            background: `linear-gradient(135deg, ${tag.color} 0%, ${tag.color}cc 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 4px 12px ${tag.color}40`,
                          }}
                        >
                          <LabelIcon sx={{ color: '#fff', fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 700,
                              fontSize: '1.1rem',
                              color: '#2c3e50',
                              mb: 0.5,
                              lineHeight: 1.2,
                            }}
                          >
                            {getLanguageText(tag.name, currentLanguage) || 'Unnamed Tag'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: 14, color: tag.color }} />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: tag.color,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            >
                              {assignedLocations.length} Location{assignedLocations.length !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      
                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Assign locations" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenAssignLocationsDialog(tag)}
                            sx={{ 
                              backgroundColor: '#e8f5e9',
                              color: '#2ecc71',
                              '&:hover': {
                                backgroundColor: '#2ecc71',
                                color: '#fff',
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s',
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit tag" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(tag)}
                            sx={{ 
                              backgroundColor: `${buttonColor}15`,
                              color: buttonColor,
                              '&:hover': {
                                backgroundColor: buttonColor,
                                color: '#fff',
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s',
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete tag" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTag(tag._id)}
                            sx={{ 
                              backgroundColor: '#ffebee',
                              color: '#e74c3c',
                              '&:hover': {
                                backgroundColor: '#e74c3c',
                                color: '#fff',
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s',
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    {/* Description */}
                    {tag.description && getLanguageText(tag.description, currentLanguage) && (
                      <Box
                        sx={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: 2,
                          p: 1.5,
                          mb: 2,
                          borderLeft: `3px solid ${tag.color}`,
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#5a6c7d',
                            fontSize: '0.875rem',
                            lineHeight: 1.6,
                          }}
                        >
                          {getLanguageText(tag.description, currentLanguage)}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Locations Section */}
                    {assignedLocations.length > 0 ? (
                      <Box sx={{ mt: 2 }}>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            cursor: 'pointer',
                            backgroundColor: `${tag.color}08`,
                            borderRadius: 2,
                            p: 1.5,
                            mb: 1,
                            border: `1px solid ${tag.color}20`,
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: `${tag.color}15`,
                              borderColor: `${tag.color}40`,
                            },
                          }}
                          onClick={() => handleToggleExpand(tag._id)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                backgroundColor: `${tag.color}20`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <LocationOnIcon sx={{ fontSize: 18, color: tag.color }} />
                            </Box>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#2c3e50' }}>
                              {assignedLocations.length} Assigned
                            </Typography>
                          </Box>
                          <IconButton 
                            size="small"
                            sx={{
                              backgroundColor: `${tag.color}15`,
                              color: tag.color,
                              '&:hover': {
                                backgroundColor: `${tag.color}30`,
                              },
                            }}
                          >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </Box>
                        
                        <Collapse in={isExpanded}>
                          <List dense sx={{ mt: 1, maxHeight: 220, overflow: 'auto', pr: 0.5 }}>
                            {assignedLocations.slice(0, 5).map((location, idx) => (
                              <ListItem 
                                key={location._id}
                                sx={{
                                  backgroundColor: '#fff',
                                  mb: 1,
                                  borderRadius: 2,
                                  border: '1px solid #e8ecef',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    backgroundColor: `${tag.color}08`,
                                    borderColor: `${tag.color}40`,
                                    transform: 'translateX(4px)',
                                  },
                                }}
                                secondaryAction={
                                  <Tooltip title="Remove tag" arrow>
                                    <IconButton 
                                      edge="end" 
                                      size="small"
                                      onClick={() => handleRemoveTagFromLocation(tag._id, location._id)}
                                      sx={{ 
                                        color: '#95a5a6',
                                        '&:hover': {
                                          color: '#e74c3c',
                                          backgroundColor: '#ffebee',
                                        },
                                      }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                }
                              >
                                <ListItemText 
                                  primary={getLanguageText(location.name, currentLanguage) || 'Unnamed Location'}
                                  primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: '#34495e',
                                  }}
                                />
                              </ListItem>
                            ))}
                            {assignedLocations.length > 5 && (
                              <Box sx={{ textAlign: 'center', mt: 1 }}>
                                <Button
                                  size="small"
                                  onClick={() => handleViewLocations(tag)}
                                  sx={{ 
                                    color: tag.color,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '&:hover': {
                                      backgroundColor: `${tag.color}15`,
                                    },
                                  }}
                                >
                                  View all {assignedLocations.length} locations →
                                </Button>
                              </Box>
                            )}
                          </List>
                        </Collapse>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: 2,
                          p: 2,
                          textAlign: 'center',
                          border: '1px dashed #dee2e6',
                        }}
                      >
                        <LocationOnIcon sx={{ fontSize: 32, color: '#adb5bd', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          No locations assigned yet
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenAssignLocationsDialog(tag)}
                          sx={{
                            mt: 1,
                            color: tag.color,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: `${tag.color}15`,
                            },
                          }}
                        >
                          Assign Locations
                        </Button>
                      </Box>
                    )}
                    
                    {/* Footer */}
                    <Box 
                      sx={{ 
                        mt: 2.5,
                        pt: 2,
                        borderTop: '1px solid #e8ecef',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#95a5a6',
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        Created {new Date(tag.createdAt).toLocaleDateString()}
                      </Typography>
                      <Chip
                        size="small"
                        label={tag.color}
                        sx={{
                          backgroundColor: `${tag.color}15`,
                          color: tag.color,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 22,
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create/Edit Tag Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {editingTag ? 'Edit Tag' : 'Create New Tag'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Tag Name"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              sx={{ mb: 3 }}
              required
            />
            
            
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Tag Color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {colorOptions.map((color) => (
                <Box
                  key={color}
                  onClick={() => setTagColor(color)}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: tagColor === color ? '3px solid #000' : '2px solid #ddd',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                />
              ))}
            </Box>
            
            <TextField
              fullWidth
              label="Custom Color (Hex)"
              value={tagColor}
              onChange={(e) => setTagColor(e.target.value)}
              placeholder="#3498db"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveTag}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: buttonColor,
              '&:hover': {
                backgroundColor: buttonColor,
                opacity: 0.9,
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : editingTag ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={openBulkAssignDialog} onClose={handleCloseBulkAssignDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Bulk Assign Tags to Locations</Typography>
            <IconButton onClick={handleCloseBulkAssignDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select Tags
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={tags}
              getOptionLabel={(option) => getLanguageText(option.name, currentLanguage) || 'Unnamed Tag'}
              value={selectedTags}
              onChange={(event, newValue) => setSelectedTags(newValue)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search and select tags..." />
              )}
              renderOption={(props, option, { selected }) => (
                <li 
                  {...props}
                  style={{
                    ...props.style,
                    backgroundColor: selected ? `${option.color}20` : 'transparent',
                    fontWeight: selected ? 600 : 400,
                  }}
                >
                  <Chip
                    label={getLanguageText(option.name, currentLanguage) || 'Unnamed Tag'}
                    size="small"
                    sx={{
                      backgroundColor: option.color,
                      color: '#fff',
                    }}
                  />
                </li>
              )}
              sx={{ mb: 3 }}
            />
            
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select Locations
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={locations}
              getOptionLabel={(option) => getLanguageText(option.name, currentLanguage) || 'Unnamed Location'}
              value={selectedLocations}
              onChange={(event, newValue) => setSelectedLocations(newValue)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search and select locations..." />
              )}
              renderOption={(props, option, { selected }) => (
                <li 
                  {...props}
                  style={{
                    ...props.style,
                    backgroundColor: selected ? `${buttonColor}20` : 'transparent',
                    fontWeight: selected ? 600 : 400,
                  }}
                >
                  {getLanguageText(option.name, currentLanguage) || 'Unnamed Location'}
                </li>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkAssignDialog}>Cancel</Button>
          <Button
            onClick={handleBulkAssign}
            variant="contained"
            disabled={loading || selectedTags.length === 0 || selectedLocations.length === 0}
            sx={{
              backgroundColor: buttonColor,
              '&:hover': {
                backgroundColor: buttonColor,
                opacity: 0.9,
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Assign Tags'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Locations to Tag Dialog */}
      <Dialog 
        open={openAssignLocationsDialog} 
        onClose={handleCloseAssignLocationsDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">Assign Locations to:</Typography>
              {selectedTagForAssignment && (
                <Chip
                  label={getLanguageText(selectedTagForAssignment.name, currentLanguage) || 'Unnamed Tag'}
                  sx={{
                    backgroundColor: selectedTagForAssignment.color,
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
            <IconButton onClick={handleCloseAssignLocationsDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select Locations
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={locations}
              getOptionLabel={(option) => getLanguageText(option.name, currentLanguage) || 'Unnamed Location'}
              value={locationsToAssign}
              onChange={(event, newValue) => setLocationsToAssign(newValue)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search and select locations..." />
              )}
              renderOption={(props, option, { selected }) => (
                <li 
                  {...props}
                  style={{
                    ...props.style,
                    backgroundColor: selected ? `${selectedTagForAssignment?.color || buttonColor}20` : 'transparent',
                    fontWeight: selected ? 600 : 400,
                  }}
                >
                  {getLanguageText(option.name, currentLanguage) || 'Unnamed Location'}
                </li>
              )}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Selected: {locationsToAssign.length} location{locationsToAssign.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignLocationsDialog}>Cancel</Button>
          <Button
            onClick={handleAssignLocationsToTag}
            variant="contained"
            disabled={loading || locationsToAssign.length === 0}
            sx={{
              backgroundColor: selectedTagForAssignment?.color || buttonColor,
              '&:hover': {
                backgroundColor: selectedTagForAssignment?.color || buttonColor,
                opacity: 0.9,
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Assign Locations'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View All Locations Dialog */}
      <Dialog 
        open={openLocationsDialog} 
        onClose={handleCloseLocationsDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">Locations with Tag:</Typography>
              {selectedTagForLocations && (
                <Chip
                  label={getLanguageText(selectedTagForLocations.name, currentLanguage) || 'Unnamed Tag'}
                  sx={{
                    backgroundColor: selectedTagForLocations.color,
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
            <IconButton onClick={handleCloseLocationsDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTagForLocations && (
            <Box sx={{ mt: 2 }}>
              {tagLocations[selectedTagForLocations._id]?.length > 0 ? (
                <List>
                  {tagLocations[selectedTagForLocations._id].map((location, index) => (
                    <ListItem 
                      key={location._id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff',
                        mb: 1,
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}
                      secondaryAction={
                        <Tooltip title="Remove tag from this location">
                          <IconButton 
                            edge="end"
                            onClick={() => {
                              handleRemoveTagFromLocation(selectedTagForLocations._id, location._id);
                              // Update the dialog if this was the last location
                              if (tagLocations[selectedTagForLocations._id].length === 1) {
                                handleCloseLocationsDialog();
                              }
                            }}
                            sx={{ color: '#e74c3c' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemText 
                        primary={getLanguageText(location.name, currentLanguage) || 'Unnamed Location'}
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            {location.description && (
                              <Typography variant="caption" display="block">
                                {location.description}
                              </Typography>
                            )}
                            {location.spaceName && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Space: {location.spaceName}
                              </Typography>
                            )}
                          </Box>
                        }
                        primaryTypographyProps={{
                          fontWeight: 600,
                          fontSize: '1rem',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">
                  No locations assigned to this tag
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLocationsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagManager;
